import { RECIPE_COURSES, formatCourse, type RecipeCourse } from '@mlc/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type { CreateRecipeRequest, CreateRecipeResponse, Difficulty, RecipeImportPrefill } from '@mlc/shared';
// Réutilisation directe du parseur d'ingrédients serveur (§3.2) pour l'aperçu
// structuré en direct (US-01). Fonction pure, sans dépendance Node : elle
// s'exécute aussi bien dans le navigateur que côté serveur, où la même
// fonction reparse la ligne à l'enregistrement — jamais deux implémentations
// à maintenir en parallèle.
import { parseIngredientLine } from '@mlc/server/src/ingredients/parseIngredientLine';
import { OfflineQueuedError, createRecipe, listCuisines } from './api';
import {Badge, Banner, Button, Icon, Select, fieldClasses } from './ui';

export interface RecipeFormProps {
  onCreated: (recipe: CreateRecipeResponse) => void;
  /**
   * Hors ligne (US-12, AC US-01 : « l'enregistrement fonctionne hors
   * connexion ») : l'écriture est mise en file plutôt que perdue —
   * `onCreated` ne peut pas être appelé (pas de recette renvoyée par le
   * serveur), cette variante l'est à la place.
   */
  onQueuedOffline?: () => void;
  onCancel: () => void;
  /**
   * Pré-remplissage depuis un import — URL (US-02) ou texte collé (US-03).
   * `null`/absent : saisie manuelle vierge (US-01). Le brief ne prévoit qu'un
   * seul écran « Ajout / édition », « formulaire unique servant à la saisie
   * manuelle et à la validation d'import » (§6) : `RecipeForm` reste ce seul
   * écran dans les deux cas, seul son état initial change.
   */
  initial?: RecipeImportPrefill | null;
  /** Ouvre la fiche d'une recette existante détectée comme doublon probable (US-02, §4.4 étape 6). */
  onViewDuplicate?: (recipeId: string) => void;
  /**
   * Enregistrement alternatif (US-71). Absent : `createRecipe`, le chemin de
   * toujours.
   *
   * Le sas en fournit un autre — `POST /api/import/draft/:id/garder` — pour que
   * « Ouvrir pour corriger » reste **un seul geste** : la recette entre, la
   * photo capturée suit, et l'entrée disparaît du sas. Passer par
   * `createRecipe` aurait laissé la photo derrière (le navigateur n'en a pas
   * les octets) et l'entrée en double jusqu'à un nettoyage ultérieur.
   *
   * Une prop plutôt qu'un branchement interne : cet écran ne connaît pas le
   * sas, et n'a aucune raison de le connaître. Il valide un préremplissage et
   * appelle ce qu'on lui a donné.
   */
  soumettre?: (body: CreateRecipeRequest) => Promise<CreateRecipeResponse>;
}

/** Une entrée par ligne, lignes vides ignorées — même découpage que le serveur (§6). */
function splitLines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function toNullableNumber(value: string): number | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : Number(trimmed);
}

function toNullableText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

// Style de champ partagé (B1, refonte UX/UI passe 3) : voir ui/Field.tsx.
const inputClass = fieldClasses('default');
const labelClass = 'flex flex-col gap-1 text-sm';

function numberOrEmpty(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

/**
 * Un bloc du formulaire, et son état réel. `warning` (US-26) est le troisième
 * état, celui qui manquait : la progression affichait « 3/3 ✓ » alors que
 * trois ingrédients sur cinq portaient un badge « à vérifier ». Un indicateur
 * qui annonce « tout est bon » quand ce n'est pas le cas est pire que pas
 * d'indicateur du tout.
 */
interface ProgressStep {
  label: string;
  done: boolean;
  /** Renseigné, mais avec des lignes que le parseur n'a pas su lire. */
  warning?: boolean;
}

/**
 * Repères de progression (audit UX/UI passe 2, I3 : « aucun indicateur de
 * progression n'existe », §9 de la mission). Un formulaire à défilement
 * unique reste la forme retenue pour cette passe (le regroupement en
 * sections repliables n'était qu'une piste, « à envisager », dans l'audit) ;
 * ces repères rendent au moins visible ce qui reste à remplir sans imposer
 * de navigation par étapes.
 */
function FormProgress({ steps }: { steps: ProgressStep[] }): React.JSX.Element {
  const doneCount = steps.filter((step) => step.done).length;
  const toCheck = steps.filter((step) => step.warning === true);
  return (
    <div aria-label="Progression du formulaire" className="flex flex-col gap-2">
      <p className="text-label">
        Progression <span className="text-data normal-case tracking-normal text-foreground-muted">{doneCount}/{steps.length}</span>
        {toCheck.length > 0 && (
          <span className="ml-2 normal-case tracking-normal text-warning">
            — {toCheck.map((step) => step.label.toLowerCase()).join(' et ')} à vérifier
          </span>
        )}
      </p>
      <ol className="flex flex-wrap gap-2">
        {steps.map((step) => (
          <li
            key={step.label}
            className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
              step.warning === true
                ? 'bg-warning-bg text-warning'
                : step.done
                  ? 'bg-success-bg text-success'
                  : 'bg-surface-raised text-foreground-muted'
            }`}
          >
            <Icon name={step.warning === true ? 'alerte' : step.done ? 'coche' : 'cercle'} size={0.9} />
            {step.label}
          </li>
        ))}
      </ol>
    </div>
  );
}

export function RecipeForm({
  onCreated,
  onQueuedOffline,
  onCancel,
  initial = null,
  onViewDuplicate,
  soumettre,
}: RecipeFormProps): React.JSX.Element {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [sourceName, setSourceName] = useState(initial?.source_name ?? '');
  const [author, setAuthor] = useState(initial?.author ?? '');
  const [servings, setServings] = useState(() => numberOrEmpty(initial?.servings));
  const [servingsLabel, setServingsLabel] = useState(initial?.servings_label ?? '');
  const [prepMin, setPrepMin] = useState(() => numberOrEmpty(initial?.prep_min));
  const [restMin, setRestMin] = useState(() => numberOrEmpty(initial?.rest_min));
  const [cookMin, setCookMin] = useState(() => numberOrEmpty(initial?.cook_min));
  const [difficulty, setDifficulty] = useState('');
  // Nature du plat et cuisine (US-55). Vides par défaut : on ne devine pas,
  // et « Plat » présélectionné serait un rangement faux mais crédible.
  const [course, setCourse] = useState<string>(initial?.course ?? '');
  const [cuisine, setCuisine] = useState(initial?.cuisine ?? '');
  const [cuisinesConnues, setCuisinesConnues] = useState<string[]>([]);
  useEffect(() => {
    let vivant = true;
    // Suggestions de saisie, **jamais bloquantes** : un échec laisse la liste
    // vide et le champ reste libre. Le `Promise.resolve()` en tête n'est pas
    // décoratif — il transforme une exception synchrone en rejet, sans quoi un
    // `fetch` indisponible ferait tomber tout le formulaire au montage.
    Promise.resolve()
      .then(() => listCuisines())
      .then((noms) => { if (vivant) setCuisinesConnues(noms); })
      .catch(() => { /* liste vide : la cuisine reste un champ libre */ });
    return () => { vivant = false; };
  }, []);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [ingredientsText, setIngredientsText] = useState(initial?.ingredients_text ?? '');
  const [stepsText, setStepsText] = useState(initial?.steps_text ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicateDismissed, setDuplicateDismissed] = useState(false);

  // Import seulement (US-02) : `source_url` n'est jamais saisie à la main —
  // « la première n'est éditable qu'à l'import » (commentaire de
  // `UpdateRecipeRequest`, shared/src/api.ts) — affichée en lecture seule ci-dessous.
  const sourceUrl = initial?.source_url ?? null;
  const imageUrl = initial?.image_url ?? null;
  const duplicateWarning = initial?.duplicate_warning ?? null;

  // Notes issues d'un import, et non saisies à la main : le bandeau
  // d'explication ne s'affiche que dans ce cas, et disparaît dès que
  // l'utilisateur touche au champ.
  const importedNotes = initial?.notes ?? null;
  const movedToNotes = importedNotes !== null && notes === importedNotes;

  // Aperçu structuré en direct (US-01) : reparsé à chaque frappe, jamais
  // envoyé au serveur — seul le texte brut des zones multilignes est soumis.
  const ingredientPreview = useMemo(
    () => splitLines(ingredientsText).map((line) => ({ line, parsed: parseIngredientLine(line) })),
    [ingredientsText],
  );
  const stepPreview = useMemo(() => splitLines(stepsText), [stepsText]);

  /**
   * Le bouton reste **actif** même si le titre manque (US-20). Il était grisé,
   * sans message, sans lien vers le champ concerné — situé trois écrans plus
   * haut. « Je clique et rien ne se passe » est le pire retour possible dans
   * un formulaire long : mieux vaut cliquer et être amené à l'erreur.
   */
  const titleRef = useRef<HTMLInputElement>(null);
  const [titleError, setTitleError] = useState<string | null>(null);

  const canSubmit = !submitting;

  // Protection contre la perte de saisie (audit UX/UI passe 2, I3 : « aucune
  // protection contre une fermeture accidentelle de l'onglet en cours de
  // saisie »). Se déclenche dès qu'un champ porte du contenu — un titre seul
  // suffit à représenter un travail qu'on ne veut pas perdre.
  const hasUnsavedContent =
    title.trim().length > 0 ||
    description.trim().length > 0 ||
    ingredientsText.trim().length > 0 ||
    stepsText.trim().length > 0 ||
    notes.trim().length > 0 ||
    author.trim().length > 0;

  useEffect(() => {
    if (!hasUnsavedContent) return;
    function handleBeforeUnload(event: BeforeUnloadEvent): void {
      event.preventDefault();
      // Chaîne ignorée par tous les navigateurs modernes (message générique
      // imposé par le navigateur), affectée par convention pour Firefox.
      event.returnValue = '';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => { window.removeEventListener('beforeunload', handleBeforeUnload); };
  }, [hasUnsavedContent]);

  // Repères de progression (I3 ci-dessus, `FormProgress`) : les trois blocs
  // de contenu réel du formulaire — le reste (portions, temps, difficulté…)
  // est facultatif et non structurant.
  // « À vérifier » l'emporte sur « fait » (US-26) : la progression affichait
  // « 3/3 ✓ » alors que plusieurs lignes n'avaient pas été comprises.
  const unrecognizedCount = ingredientPreview.filter(
    ({ line, parsed }) => parsed.quantity === null && parsed.unit === null && parsed.name === line,
  ).length;
  const progressSteps: ProgressStep[] = [
    { label: 'Titre', done: title.trim().length > 0 },
    {
      label: 'Ingrédients',
      done: ingredientsText.trim().length > 0,
      warning: unrecognizedCount > 0,
    },
    { label: 'Étapes', done: stepsText.trim().length > 0 },
  ];

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!canSubmit) return;

    if (title.trim().length === 0) {
      setTitleError('Donnez un titre à cette recette pour l’enregistrer.');
      const field = titleRef.current;
      // `scrollIntoView` n'existe pas partout (jsdom, navigateurs anciens) :
      // le défilement est un confort, le focus est l'essentiel.
      if (typeof field?.scrollIntoView === 'function') field.scrollIntoView({ block: 'center' });
      field?.focus();
      return;
    }

    const body: CreateRecipeRequest = {
      title: title.trim(),
      description: toNullableText(description),
      source_url: sourceUrl,
      source_name: toNullableText(sourceName),
      author: toNullableText(author),
      // Copie locale de la photo de la page (US-27, §3.4 étape 5) : c'est le
      // serveur qui la télécharge, à l'enregistrement, pour que rien ne
      // traîne derrière un import abandonné.
      image_url: imageUrl,
      servings: toNullableNumber(servings),
      servings_label: toNullableText(servingsLabel),
      prep_min: toNullableNumber(prepMin),
      rest_min: toNullableNumber(restMin),
      cook_min: toNullableNumber(cookMin),
      difficulty: (difficulty === '' ? null : Number(difficulty)) as Difficulty | null,
      course: (course === '' ? null : course) as RecipeCourse | null,
      cuisine: toNullableText(cuisine),
      notes: toNullableText(notes),
      ingredients_text: ingredientsText,
      steps_text: stepsText,
    };

    setSubmitting(true);
    setError(null);
    (soumettre ?? createRecipe)(body)
      .then(onCreated)
      .catch((err: unknown) => {
        if (err instanceof OfflineQueuedError) {
          onQueuedOffline?.();
          return;
        }
        setError(err instanceof Error ? err.message : 'Erreur inconnue.');
      })
      .finally(() => { setSubmitting(false); });
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6" aria-label="Ajouter une recette">
      <FormProgress steps={progressSteps} />

      {error !== null && <Banner tone="danger">{error}</Banner>}

      {/* US-70 — ce préremplissage a été lu par un modèle, et l'écran le dit.
          Une extraction par modèle n'est pas moins fiable qu'un découpage par
          règles, elle l'est **autrement** : les règles laissent un champ vide
          quand elles ne comprennent pas, un modèle propose une valeur
          plausible. Les quantités sont l'endroit où cette différence coûte le
          plus cher, puisqu'une quantité fausse est indiscernable d'une
          quantité lue (§3 : « faux mais crédible »). Ton `info` et non
          `warning` : l'alerte de doublon juste en dessous est le seul
          avertissement de cet écran, et deux tons d'alerte empilés n'en font
          plus aucun. */}
      {initial?.origine === 'ia' && (
        <Banner tone="info">
          Ce texte a été lu par l’IA — relisez les quantités avant d’enregistrer.
        </Banner>
      )}

      {/* Alerte non bloquante (US-02, §4.4 étape 6) : jamais de blocage, l'utilisateur reste libre d'enregistrer quand même. */}
      {duplicateWarning !== null && !duplicateDismissed && (
        <Banner tone="warning" as="div" className="flex flex-wrap items-center justify-between gap-3">
          <span>
            {duplicateWarning.reason === 'source_url'
              ? 'Cette page a déjà été importée : '
              : 'Une recette au titre très proche existe déjà : '}
            « {duplicateWarning.recipe_title} ».
          </span>
          <span className="flex gap-3">
            {onViewDuplicate && (
              <button
                type="button"
                onClick={() => { onViewDuplicate(duplicateWarning.recipe_id); }}
                className="underline"
              >
                Voir la recette existante
              </button>
            )}
            <button type="button" onClick={() => { setDuplicateDismissed(true); }} className="underline">
              Ignorer
            </button>
          </span>
        </Banner>
      )}

      {sourceUrl !== null && (
        <p className="text-meta">
          Lien d’origine : <a href={sourceUrl} target="_blank" rel="noreferrer" className="underline">{sourceUrl}</a>
        </p>
      )}

      {/* Photo de la page importée (US-27). Affichée depuis sa source : elle
          n'est copiée localement qu'à l'enregistrement, pour ne rien laisser
          derrière un import abandonné. */}
      {imageUrl !== null && (
        <figure className="flex items-center gap-3">
          <img
            data-testid="import-photo"
            src={imageUrl}
            alt=""
            className="aspect-[4/3] w-32 rounded-card object-cover"
          />
          <figcaption className="text-meta">
            Photo trouvée sur la page. Elle sera copiée dans votre carnet à l’enregistrement.
          </figcaption>
        </figure>
      )}

      <label className={labelClass}>
        {/* Libellé et astérisque dans un même élément (US-20) : le conteneur
            est un `flex flex-col`, si bien que l'astérisque tombait seul sur
            sa propre ligne, sous le mot « Titre » — dans les trois parcours de
            saisie. */}
        <span>Titre <span aria-hidden="true">*</span></span>
        <input
          ref={titleRef}
          className={inputClass}
          value={title}
          onChange={(e) => { setTitle(e.target.value); setTitleError(null); }}
          aria-required="true"
          aria-invalid={titleError !== null}
          aria-describedby={titleError !== null ? 'erreur-titre' : undefined}
          placeholder="ex. Tarte aux pommes de mamie"
        />
        {titleError !== null && (
          <span id="erreur-titre" role="alert" className="text-sm text-danger">
            {titleError}
          </span>
        )}
      </label>

      <label className={labelClass}>
        Description
        <textarea
          className={inputClass}
          value={description}
          onChange={(e) => { setDescription(e.target.value); }}
          rows={2}
        />
      </label>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <label className={labelClass}>
          Portions
          <input
            className={inputClass}
            type="number"
            inputMode="decimal"
            value={servings}
            onChange={(e) => { setServings(e.target.value); }}
          />
        </label>
        <label className={labelClass}>
          Unité de portions
          <input
            className={inputClass}
            value={servingsLabel}
            onChange={(e) => { setServingsLabel(e.target.value); }}
            placeholder="personnes"
          />
        </label>
        <label className={labelClass}>
          Nature du plat
          {/* Liste fermée (US-55) : « dessert » doit vouloir dire la même chose
              pour tout le monde, sinon une collection de desserts n'a aucun
              sens. La cuisine, elle, reste libre — le monde est plus grand
              qu'une liste. */}
          <Select aria-label="Nature du plat" value={course} onChange={(e) => { setCourse(e.target.value); }}>
            <option value="">—</option>
            {RECIPE_COURSES.map((valeur) => (
              <option key={valeur} value={valeur}>{formatCourse(valeur)}</option>
            ))}
          </Select>
        </label>
        <label className={labelClass}>
          Cuisine
          <input
            list="mlc-cuisines"
            className={fieldClasses()}
            value={cuisine}
            onChange={(e) => { setCuisine(e.target.value); }}
            placeholder="italienne, alsacienne…"
          />
          {/* Suggestions tirées des recettes **visibles** : la liste est
              filtrée côté serveur, une cuisine qui n'existerait que dans une
              recette privée d'un autre en prouverait l'existence (US-46,
              vecteur 4). */}
          <datalist id="mlc-cuisines">
            {cuisinesConnues.map((nom) => <option key={nom} value={nom} />)}
          </datalist>
        </label>
        <label className={labelClass}>
          Difficulté
          {/* Libellés en toutes lettres (US-25) : « 1 » ne veut rien dire sans
              légende, et il n'y en avait aucune. */}
          <Select aria-label="Difficulté" value={difficulty} onChange={(e) => { setDifficulty(e.target.value); }}>
            <option value="">—</option>
            <option value="1">Facile</option>
            <option value="2">Moyen</option>
            <option value="3">Difficile</option>
          </Select>
        </label>
        {/* Trois champs distincts depuis US-27 : « Source » recevait le
            pseudonyme de l'auteur, si bien que le site n'apparaissait nulle
            part et que le lien figurait dans un troisième endroit. */}
        <label className={labelClass}>
          Source
          <input
            className={inputClass}
            value={sourceName}
            onChange={(e) => { setSourceName(e.target.value); }}
            placeholder="ex. marmiton.org, Le Grand Livre de Cuisine"
          />
        </label>
        <label className={labelClass}>
          Auteur
          <input
            className={inputClass}
            value={author}
            onChange={(e) => { setAuthor(e.target.value); }}
            placeholder="ex. Mamie Odette"
          />
        </label>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <label className={labelClass}>
          Préparation (min)
          <input
            className={inputClass}
            type="number"
            inputMode="numeric"
            value={prepMin}
            onChange={(e) => { setPrepMin(e.target.value); }}
          />
        </label>
        <label className={labelClass}>
          Repos (min)
          <input
            className={inputClass}
            type="number"
            inputMode="numeric"
            value={restMin}
            onChange={(e) => { setRestMin(e.target.value); }}
          />
        </label>
        <label className={labelClass}>
          Cuisson (min)
          <input
            className={inputClass}
            type="number"
            inputMode="numeric"
            value={cookMin}
            onChange={(e) => { setCookMin(e.target.value); }}
          />
        </label>
      </div>

      <label className={labelClass}>
        Ingrédients — une ligne par ingrédient
        <textarea
          className={`${inputClass} font-mono`}
          value={ingredientsText}
          onChange={(e) => { setIngredientsText(e.target.value); }}
          rows={6}
          placeholder={'200 g de farine T55\n1/2 oignon, finement émincé\nSel, poivre'}
        />
      </label>

      {ingredientPreview.length > 0 && (
        <ul aria-label="Aperçu des ingrédients" data-testid="ingredient-preview" className="flex flex-col gap-1 text-sm">
          {ingredientPreview.map(({ line, parsed }, index) => {
            // Règle de repli du parseur (§3.2 CLAUDE.md) : une ligne non reconnue
            // est conservée telle quelle dans `name`, jamais devinée. Le badge
            // « à vérifier » rend ce comportement rassurant plutôt qu'inquiétant
            // (§9 de la mission) — le texte source est visible juste à côté,
            // rien n'est perdu.
            const unrecognized = parsed.quantity === null && parsed.unit === null && parsed.name === line;
            return (
              // La ligne source est une clé stable en pratique (une saisie manuelle rarement dupliquée à l'identique) ;
              // l'index en secours évite un crash si deux lignes sont identiques.
              <li
                key={`${index}-${line}`}
                className={`flex flex-wrap items-center gap-2 rounded-control border px-2 py-1 ${unrecognized ? 'border-warning bg-warning-bg' : 'border-border'}`}
              >
                <span className="text-data w-16 shrink-0 text-foreground-muted">{parsed.quantity ?? '—'}</span>
                <span className="text-data w-20 shrink-0 text-foreground-muted">{parsed.unit ?? '—'}</span>
                <span className="font-medium text-foreground">{parsed.name}</span>
                {parsed.note !== null && <span className="text-foreground-muted">({parsed.note})</span>}
                {parsed.is_optional === 1 && <span className="text-foreground-muted">facultatif</span>}
                {unrecognized && (
                  <span className="ml-auto flex shrink-0 items-center gap-2">
                    <Badge tone="warning">à vérifier</Badge>
                    <span className="text-meta">texte non reconnu, conservé tel quel</span>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <label className={labelClass}>
        Étapes — une ligne par étape
        <textarea
          className={`${inputClass} font-mono`}
          value={stepsText}
          onChange={(e) => { setStepsText(e.target.value); }}
          rows={6}
          placeholder={'Préchauffer le four à 180°C\nÉtaler la pâte\nEnfourner 25 minutes'}
        />
      </label>

      {stepPreview.length > 0 && (
        <ol aria-label="Aperçu des étapes" data-testid="step-preview" className="flex list-decimal flex-col gap-1 pl-5 text-sm">
          {stepPreview.map((line, index) => (
            <li key={`${index}-${line}`}>{line}</li>
          ))}
        </ol>
      )}

      <label className={labelClass}>
        Notes personnelles
        {/* Ce qui a été déplacé ici est signalé (US-26). Le texte collé y était
            versé en silence : l'utilisateur retrouvait le titre de son message
            dans ses notes sans comprendre pourquoi. */}
        {movedToNotes && (
          <span className="flex items-start gap-2 text-xs text-foreground-secondary">
            <Badge tone="warning">déplacé ici</Badge>
            <span>
              Ces lignes du texte collé n’ont pu être classées ni en ingrédient ni en étape. Rien n’est perdu :
              déplacez-les où il faut, ou laissez-les là.
            </span>
          </span>
        )}
        <textarea className={inputClass} value={notes} onChange={(e) => { setNotes(e.target.value); }} rows={2} />
      </label>

      <div className="flex gap-3">
        {/* §4 : le libellé ne change pas pendant l'action — `aria-busy` et
            `disabled` disent que le bouton travaille, le mot reste. */}
        <Button type="submit" variant="primary" disabled={!canSubmit} aria-busy={submitting}>
          Enregistrer la recette
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Annuler
        </Button>
      </div>
    </form>
  );
}
